import {NewTaskChatInput} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'
import {useCreateEnvironmentTask} from '@github-ui/agent-sessions/hooks/use-create-environment-task'
import {useCreateLocalDeviceSession} from '@github-ui/agent-sessions/hooks/use-create-local-device-session'
import type {DropdownRepository} from '@github-ui/agent-sessions/hooks/useCopilotRepoFetching'
import {
  type FetchTasksResponse,
  getAgentTasksQueryKeyForMutation,
  getFlatTaskList,
  PAGE_SIZE,
  setFlatTaskList,
} from '@github-ui/agent-sessions/hooks/useTasks'
import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions/types/agent-quota-snapshot'
import type {IntentDetectionMode} from '@github-ui/agent-sessions/types/intent-detection-mode'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useMemo} from 'react'

import styles from './TasksInChatInput.module.css'

type TasksInChatInputProps = {
  agentQuotaSnapshot?: AgentQuotaSnapshot
  handleIntentDetectionSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    selectedMode?: IntentDetectionMode,
    modelId?: string,
    createPullRequest?: boolean,
  ) => Promise<void>
}
export function TasksInChatInput({agentQuotaSnapshot, handleIntentDetectionSubmit}: TasksInChatInputProps) {
  // Mirror the optimistic-list config used by every other consumer of
  // `NewAgentTaskForm` on the immersive Copilot pages so that local-device
  // task submissions land in the same cache key as cloud submissions and
  // appear at the top of the "Recent agent sessions" list.
  const taskListMutationConfig = useMemo(
    () => ({
      queryKey: getAgentTasksQueryKeyForMutation(),
      pageSize: PAGE_SIZE,
      getFlatTaskList,
      setFlatTaskList,
    }),
    [],
  )
  const localDeviceMutation = useCreateLocalDeviceSession<FetchTasksResponse>({
    optimisticTaskListConfig: taskListMutationConfig,
  })
  const onLocalDeviceSubmit = localDeviceMutation.mutateAsync

  const navigate = useNavigate()
  const {mutateAsync: createEnvironmentTask} = useCreateEnvironmentTask<FetchTasksResponse>({
    optimisticTaskListConfig: taskListMutationConfig,
  })
  const onEnvironmentSubmit = useCallback(
    async ({environmentId, prompt}: {environmentId: string; prompt: string}) => {
      const {task} = await createEnvironmentTask({environmentId, problemStatement: prompt})
      navigate(generateTaskPathForRepo({taskId: task.id}))
    },
    [createEnvironmentTask, navigate],
  )

  return (
    <>
      <div className={styles.intentDetectionContainer}>
        <NewTaskChatInput
          eventType="chat_page"
          agentQuotaSnapshot={agentQuotaSnapshot}
          handleUserSubmit={handleIntentDetectionSubmit}
          submitWithModifier={false}
          onLocalDeviceSubmit={onLocalDeviceSubmit}
          onEnvironmentSubmit={onEnvironmentSubmit}
        />
      </div>
    </>
  )
}
